import React, { useMemo } from "react";
import { generateBarcodeDataUrl } from "@/lib/barcode";

/**
 * Landscape 3in x 1.5in sticker used for Attro India (packed & marketed) and
 * Attro China (imported & marketed) labels.
 *
 * Layout: vertical stack — text lines run full-width across the top so each
 * field can fit on a single line whenever possible, wrapping only when the
 * value is genuinely longer than the sticker. The barcode then occupies the
 * FULL width of the sticker at the bottom, giving it enough bar height and
 * width to remain scannable on a thermal roll printer.
 */
export const IndiaChinaLabel = React.memo(function IndiaChinaLabel({
  data,
  widthIn = 3,
  heightIn = 1.5,
  barcodeDataUrl,
}) {
  const p = data || {};
  const kind = p.label_type === "china" ? "china" : "india";
  const ean = (p.ean_code || "").replace(/\s+/g, "");
  const showBarcode = ean.length >= 1;

  // Always regenerate the barcode locally with tall bars so it stays scannable
  // when printed at the full-width bottom of the sticker. We deliberately
  // ignore any `barcodeDataUrl` passed by the parent for india/china — the
  // parent's version is tuned for the small square copper label.
  const barcodeUrl = useMemo(
    () =>
      showBarcode
        ? generateBarcodeDataUrl(ean, {
            width: 3,
            height: 120,
            fontSize: 22,
            textMargin: 4,
            margin: 0,
          })
        : null,
    [showBarcode, ean]
  );

  const marketedLabel = kind === "china" ? "Imported & Marketed By" : "Packed & Marketed By";

  // Font scaling: text now has full width to breathe. Slightly smaller to
  // guarantee every field (address, query email, customer care) fits on a
  // 1.5in-tall sticker.
  const fontPx = Math.max(5.4, widthIn * 1.9);

  // Barcode occupies ~32 % of the sticker height — still ~0.48in tall on a
  // 1.5in sticker (enough for scannable bars) while leaving the majority of
  // the height for the text block.
  const barcodeH = heightIn * 0.32;
  const padIn = heightIn * 0.025;
  const rowGap = heightIn * 0.002;

  // Every text line prefers to stay on ONE row; only truly long values wrap.
  const line = (label, value) => (
    <div
      style={{
        marginBottom: `${rowGap}in`,
        whiteSpace: "normal",
        overflowWrap: "anywhere",
      }}
    >
      <span style={{ fontWeight: 700 }}>{label}: </span>
      <span>{value}</span>
    </div>
  );

  return (
    <div
      className="label-sticker relative bg-white text-black"
      style={{
        width: `${widthIn}in`,
        height: `${heightIn}in`,
        boxSizing: "border-box",
        border: "1px solid #d4d4d8",
        padding: `${padIn}in`,
        overflow: "hidden",
        fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
        fontSize: `${fontPx}px`,
        lineHeight: 1.05,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* TOP: text list — takes remaining height above the barcode */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {p.product_name && (
          <div
            style={{
              marginBottom: `${rowGap}in`,
              fontWeight: 700,
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            }}
          >
            <span>Item Name: </span>
            <span>{p.product_name}</span>
          </div>
        )}
        {p.net_quantity && line("Net Content", p.net_quantity)}
        {p.actual_capacity && line("Capacity", p.actual_capacity)}
        {p.color && line("Color", p.color)}
        {kind === "china" && p.model_no && line("Model No", p.model_no)}
        {p.material_content && line("Material", p.material_content)}
        {p.mrp && (
          <div
            style={{
              marginBottom: `${rowGap}in`,
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            }}
          >
            <span style={{ fontWeight: 700 }}>MRP: </span>
            <span>
              ₹{p.mrp}
              {p.included_of_taxes !== false && "/- (Inclusive of all Taxes)"}
            </span>
          </div>
        )}
        {p.packed_on && line("Packed On", p.packed_on)}
        {p.country_of_origin && line("Country of Origin", p.country_of_origin)}
        {p.marketed_by && (
          <div
            style={{
              marginBottom: `${rowGap}in`,
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            }}
          >
            <span style={{ fontWeight: 700 }}>{marketedLabel}: </span>
            <span style={{ whiteSpace: "pre-line" }}>{p.marketed_by}</span>
          </div>
        )}
        {(p.query_email || p.customer_care) && (
          <div style={{ marginTop: `${rowGap}in`, overflowWrap: "anywhere" }}>
            {p.query_email && (
              <>
                <span style={{ fontWeight: 700 }}>For any Query contact: </span>
                <span>{p.query_email}</span>
              </>
            )}
            {p.customer_care && (
              <>
                {p.query_email && <span> · </span>}
                <span style={{ fontWeight: 700 }}>Customer Care: </span>
                <span>{p.customer_care}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* BOTTOM: full-width barcode — wide + tall for a scannable print.
          A small horizontal inset keeps the bars off the sticker edges. */}
      <div
        style={{
          width: "100%",
          height: `${barcodeH}in`,
          marginTop: `${heightIn * 0.015}in`,
          paddingLeft: `${widthIn * 0.08}in`,
          paddingRight: `${widthIn * 0.08}in`,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {showBarcode && barcodeUrl ? (
          <img
            src={barcodeUrl}
            alt={ean}
            style={{
              width: "100%",
              height: "100%",
              // "fill" stretches both axes independently. That's safe for
              // barcodes because *relative* bar widths are preserved under
              // uniform horizontal scaling — scanners only care about the
              // ratio between bar/space widths, not absolute inches. The
              // vertical stretch just makes the bars taller.
              objectFit: "fill",
              objectPosition: "center",
              imageRendering: "crisp-edges",
            }}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", background: "#f4f4f5" }} />
        )}
      </div>
    </div>
  );
});

export default IndiaChinaLabel;
